// State budget dimension — per-instrument fiscal-impact waterfall.
//
// Rendered as React-owned SVG with d3-scale (the bracket_editor.tsx pattern),
// not echarts. Belgium has no single unified state budget — instruments are
// levied at different government levels — so the waterfall groups bars by level
// (federal / regional / …) with a gap between groups and a grounded SUBTOTAL bar
// after each group, plus a final grounded Net bar. One delta bar per instrument
// cascades on the running total within its group; bar fill encodes sign (brand
// primary for gains to the budget, dark grey for costs).
//
// Why SVG rather than echarts: echarts has no first-class waterfall (it was
// emulated with transparent placeholder bars on a canvas), and a canvas gives no
// DOM hooks — so per-bar / per-subtotal `data-testid`s and accessibility titles
// weren't possible. React-owned SVG fixes both and makes the grouped-with-
// subtotals layout natural. See `notes/budget_waterfall_chart_precedents.md`.

import { useRef, useState } from "react";
import { scaleLinear } from "d3-scale";
import { dimensionByKey } from "../../lib/dimensions";
import type {
  EngineOutput,
  InstrumentBudget,
  InstrumentLevel,
} from "../../lib/types";
import { HeadlineMetric } from "../headline_metric";

// Sign-coloured bar fill. The colour channel carries the single semantic
// "does this add to or cost the budget"; tier is carried in the hover tooltip.
const GAIN_BAR_COLOUR = "#5e4a97"; // brand primary
const LOSS_BAR_COLOUR = "#6e6e70"; // muted dark grey
const NET_GAIN_COLOUR = "#7fbf69"; // brand gain (green)
const NET_LOSS_COLOUR = "#c66e5c"; // brand loss (red)
const ZERO_BAR_COLOUR = "#c9c5d6"; // unmodified / no-change

// Per-tier dot colour for the tooltip. Hue-varying so the collectors read as
// distinct categories.
const TIER_COLOURS: Record<InstrumentLevel, string> = {
  regional: "#5e4a97",
  federal: "#3d8a86",
  social_security: "#c08c3f",
  benefit: "#8a8a8d",
};

const TIER_LABELS: Record<InstrumentLevel, string> = {
  regional: "Regional",
  federal: "Federal",
  social_security: "Social security",
  benefit: "Benefit",
};

// Title-cased display labels for the engine's lower-case instrument keys.
// Anything missing falls back to the raw key.
const INSTRUMENT_LABELS: Record<string, string> = {
  vat: "VAT",
  // PIT is split federal/regional (#257); the combined "pit" id is retired.
  pit_federal: "Personal income tax (federal)",
  pit_regional: "Personal income tax (regional)",
  iit: "Investment income tax",
  excise: "Excise",
  car_tax: "Car tax",
  child_benefit: "Child benefit",
};

// Short axis labels — the long names collide when rotated, so the x-axis uses
// abbreviations while the full name stays in the hover tooltip and aria title.
// The two PIT bars sit in separate (regional / federal) groups, so position +
// the tier subtotal disambiguate them without repeating the tier in the label.
const INSTRUMENT_LABELS_SHORT: Record<string, string> = {
  vat: "VAT",
  pit_federal: "PIT",
  pit_regional: "PIT",
  iit: "IIT",
  excise: "Excise",
  car_tax: "Car tax",
  child_benefit: "Child benefit",
};

// Fixed level order for grouping. Even when a reform doesn't touch every tier we
// keep this order so a tier always sits in the same place across reforms.
const TIER_ORDER: readonly InstrumentLevel[] = [
  "regional",
  "federal",
  "social_security",
  "benefit",
];

// SVG coordinate space. Responsive via viewBox + preserveAspectRatio (CSS sizes
// the rendered box), so these are layout units, not pixels.
const WIDTH = 680;
const HEIGHT = 360;
const MARGIN = { top: 28, right: 16, bottom: 78, left: 60 };
const GAP_UNITS = 0.6; // extra band-widths of space between level groups
const BAR_FRACTION = 0.62; // bar width as a fraction of its band

interface Props {
  engineOutput: EngineOutput;
  baselineOutput: EngineOutput | null;
  isModified: boolean;
}

interface OrderedInstrument extends InstrumentBudget {
  label: string;
}

export function BudgetWaterfall({
  engineOutput,
  isModified,
}: Props): JSX.Element {
  const ordered = sortInstruments(engineOutput.instrument_budget);
  const netReform = sum(ordered.map((i) => i.delta_eur));

  return (
    <div className="beamm-budget-waterfall" data-testid="budget-waterfall">
      <div className="beamm-scope-badge" data-testid="scope-badge">
        Brussels Capital Region population only · scaled from a ~2,000-household
        sample
      </div>
      <HeadlineMetric
        dimension={dimensionByKey.state_budget}
        label="Net budget impact"
        tooltip="Net change in the public budget for the year — the sum of additional revenue raised by each instrument minus additional expenditure, across all government levels. Scaled to the full Brussels Capital Region population."
        baseline={0}
        reform={netReform}
        isModified={isModified}
        formatter={formatMillionsEur}
        deltaOnly
      />
      <div className="beamm-budget-chart-wrap">
        <SignLegend isModified={isModified} netReform={netReform} />
        <WaterfallChart
          ordered={ordered}
          netReform={netReform}
          isModified={isModified}
        />
      </div>
    </div>
  );
}

function SignLegend({
  isModified,
  netReform,
}: {
  isModified: boolean;
  netReform: number;
}): JSX.Element {
  const netColour = !isModified
    ? ZERO_BAR_COLOUR
    : netReform >= 0
      ? NET_GAIN_COLOUR
      : NET_LOSS_COLOUR;
  return (
    <div className="beamm-sign-legend" data-testid="sign-legend">
      <span className="beamm-sign-legend-item">
        <span
          className="beamm-sign-legend-swatch"
          style={{ background: GAIN_BAR_COLOUR }}
          aria-hidden="true"
        />
        Adds to budget
      </span>
      <span className="beamm-sign-legend-item">
        <span
          className="beamm-sign-legend-swatch"
          style={{ background: LOSS_BAR_COLOUR }}
          aria-hidden="true"
        />
        Reduces budget
      </span>
      <span className="beamm-sign-legend-item">
        <span
          className="beamm-sign-legend-swatch"
          style={{ background: netColour }}
          aria-hidden="true"
        />
        Net
      </span>
    </div>
  );
}

// One laid-out column of the waterfall. `lo`/`hi` are the value-space extent of
// the bar (cascade bars span the running total step; grounded bars span 0..value).
interface Column {
  kind: "instrument" | "subtotal" | "net";
  key: string; // stable id for React + the data-testid
  testid: string;
  label: string;
  level?: InstrumentLevel;
  // Display value (instrument delta, group subtotal, or net) for the tooltip.
  delta: number;
  baselineEur?: number;
  reformEur?: number;
  lo: number;
  hi: number;
  gapAfter: boolean;
  // Filled after x layout.
  cx: number;
  bandW: number;
  // Running total immediately after this column (instruments only; for connectors).
  after?: number;
}

function buildColumns(
  ordered: OrderedInstrument[],
  netReform: number,
  isModified: boolean,
): Column[] {
  const cols: Column[] = [];
  // Levels present, in TIER_ORDER (ordered is already sorted by it).
  const levels = TIER_ORDER.filter((l) => ordered.some((i) => i.level === l));
  let running = 0;
  for (const level of levels) {
    // Drop instruments the reform didn't move: a zero-height bar leaves an
    // orphan axis label that reads as a render gap (#3). The baseline view has
    // no deltas at all, so keep its full structure visible.
    const group = ordered.filter(
      (i) => i.level === level && (!isModified || Math.abs(i.delta_eur) >= 1),
    );
    if (group.length === 0) continue; // tier untouched — skip its bars + subtotal
    let groupDelta = 0;
    for (const inst of group) {
      const after = running + inst.delta_eur;
      cols.push({
        kind: "instrument",
        key: inst.instrument,
        testid: `budget-bar-${inst.instrument}`,
        label: inst.label,
        level,
        delta: inst.delta_eur,
        baselineEur: inst.baseline_eur,
        reformEur: inst.reform_eur,
        lo: Math.min(running, after),
        hi: Math.max(running, after),
        gapAfter: false,
        cx: 0,
        bandW: 0,
        after,
      });
      running = after;
      groupDelta += inst.delta_eur;
    }
    cols.push({
      kind: "subtotal",
      key: `subtotal-${level}`,
      testid: `budget-subtotal-${level}`,
      label: `${TIER_LABELS[level]} subtotal`,
      level,
      delta: groupDelta,
      lo: Math.min(0, groupDelta),
      hi: Math.max(0, groupDelta),
      gapAfter: true,
      cx: 0,
      bandW: 0,
    });
  }
  cols.push({
    kind: "net",
    key: "net",
    testid: "budget-bar-net",
    label: "Net",
    delta: netReform,
    lo: Math.min(0, netReform),
    hi: Math.max(0, netReform),
    gapAfter: false,
    cx: 0,
    bandW: 0,
  });
  return layoutColumns(cols);
}

// Assign each column a centre x and band width across the plot area, inserting
// GAP_UNITS of extra space after any column flagged gapAfter (the group breaks).
function layoutColumns(cols: Column[]): Column[] {
  const plotW = WIDTH - MARGIN.left - MARGIN.right;
  let units = 0;
  for (const c of cols) units += 1 + (c.gapAfter ? GAP_UNITS : 0);
  const band = plotW / units;
  let cursor = MARGIN.left;
  for (const c of cols) {
    c.cx = cursor + band / 2;
    c.bandW = band * BAR_FRACTION;
    cursor += band + (c.gapAfter ? band * GAP_UNITS : 0);
  }
  return cols;
}

interface HoverState {
  col: Column;
  x: number;
  y: number;
}

function WaterfallChart({
  ordered,
  netReform,
  isModified,
}: {
  ordered: OrderedInstrument[];
  netReform: number;
  isModified: boolean;
}): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  const cols = buildColumns(ordered, netReform, isModified);

  // Per-tier background bands + headers: the strongest immediate-scan cue for
  // which cluster is which level (the bottom "Regional ∑" label alone reads too
  // late). Header is left-aligned to stay clear of the top-right sign legend.
  const BAND_PAD = 8;
  const groupBands = TIER_ORDER.filter((l) =>
    cols.some((c) => c.level === l),
  ).map((level) => {
    const gc = cols.filter((c) => c.level === level);
    const x1 = Math.min(...gc.map((c) => c.cx - c.bandW / 2)) - BAND_PAD;
    const x2 = Math.max(...gc.map((c) => c.cx + c.bandW / 2)) + BAND_PAD;
    return { level, x1, x2 };
  });

  // y domain spans every bar extent and zero, with ~10% nice headroom each side.
  const hiPeak = Math.max(0, ...cols.map((c) => c.hi), 1);
  const loPeak = Math.min(0, ...cols.map((c) => c.lo));
  const yMax = roundUpToNice(hiPeak * 1.1);
  const yMin = loPeak < 0 ? -roundUpToNice(Math.abs(loPeak) * 1.1) : 0;

  const yScale = scaleLinear()
    .domain([yMin, yMax])
    .range([HEIGHT - MARGIN.bottom, MARGIN.top]);
  const yTicks = yScale.ticks(5);
  const zeroY = yScale(0);

  const barColour = (c: Column): string => {
    if (!isModified || Math.abs(c.delta) < 1) return ZERO_BAR_COLOUR;
    if (c.kind === "net") {
      return c.delta >= 0 ? NET_GAIN_COLOUR : NET_LOSS_COLOUR;
    }
    return c.delta >= 0 ? GAIN_BAR_COLOUR : LOSS_BAR_COLOUR;
  };

  // Connectors: dashed step lines between consecutive instrument bars within the
  // same group (a subtotal/gap between groups breaks the chain), at the running
  // total they share.
  const connectors: { x1: number; x2: number; y: number; key: string }[] = [];
  for (let i = 0; i < cols.length - 1; i++) {
    const a = cols[i];
    const b = cols[i + 1];
    if (a.kind === "instrument" && b.kind === "instrument" && a.after != null) {
      connectors.push({
        x1: a.cx + a.bandW / 2,
        x2: b.cx - b.bandW / 2,
        y: yScale(a.after),
        key: `conn-${a.key}`,
      });
    }
  }

  const onMove = (e: React.MouseEvent, col: Column) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover({ col, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div
      className="beamm-waterfall"
      ref={wrapRef}
      onMouseLeave={() => setHover(null)}
    >
      <svg
        className="beamm-waterfall__svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Budget impact by instrument, grouped by government level, with per-level subtotals and a net total."
      >
        {/* per-tier background bands + headers (behind gridlines and bars) */}
        <g>
          {groupBands.map((g) => (
            <g key={`band-${g.level}`}>
              <rect
                x={g.x1}
                y={MARGIN.top}
                width={g.x2 - g.x1}
                height={HEIGHT - MARGIN.bottom - MARGIN.top}
                rx={4}
                className="beamm-waterfall__group-band"
                fill={TIER_COLOURS[g.level]}
              />
              <text
                x={g.x1 + 6}
                y={MARGIN.top + 11}
                className="beamm-waterfall__group-label"
                fill={TIER_COLOURS[g.level]}
              >
                {TIER_LABELS[g.level].toUpperCase()}
              </text>
            </g>
          ))}
        </g>

        {/* y-axis: euro ticks + gridlines */}
        <g className="beamm-waterfall__axis">
          {yTicks.map((t) => (
            <g key={`y-${t}`} transform={`translate(0, ${yScale(t)})`}>
              <line
                x1={MARGIN.left}
                x2={WIDTH - MARGIN.right}
                className="beamm-waterfall__gridline"
              />
              <text
                x={MARGIN.left - 8}
                dy="0.32em"
                textAnchor="end"
                className="beamm-waterfall__tick"
              >
                {formatAxisMillions(t)}
              </text>
            </g>
          ))}
          <text x={4} y={12} className="beamm-waterfall__axis-label">
            Δ euros
          </text>
        </g>

        {/* zero baseline */}
        <line
          x1={MARGIN.left}
          x2={WIDTH - MARGIN.right}
          y1={zeroY}
          y2={zeroY}
          className="beamm-waterfall__zero"
        />

        {/* connectors */}
        <g>
          {connectors.map((c) => (
            <line
              key={c.key}
              x1={c.x1}
              x2={c.x2}
              y1={c.y}
              y2={c.y}
              className="beamm-waterfall__connector"
            />
          ))}
        </g>

        {/* bars */}
        <g>
          {cols.map((c) => {
            const yTop = yScale(c.hi);
            const yBot = yScale(c.lo);
            const h = Math.max(yBot - yTop, c.kind === "instrument" ? 0 : 1);
            const grounded = c.kind !== "instrument";
            return (
              <rect
                key={c.key}
                data-testid={c.testid}
                x={c.cx - c.bandW / 2}
                y={yTop}
                width={c.bandW}
                height={h}
                rx={2}
                className={
                  grounded
                    ? "beamm-waterfall__bar beamm-waterfall__bar--subtotal"
                    : "beamm-waterfall__bar"
                }
                fill={barColour(c)}
                fillOpacity={c.kind === "subtotal" ? 0.85 : 1}
                stroke={c.kind === "subtotal" ? barColour(c) : "none"}
                strokeWidth={c.kind === "subtotal" ? 1 : 0}
                onMouseEnter={(e) => onMove(e, c)}
                onMouseMove={(e) => onMove(e, c)}
              >
                <title>{`${c.label}: ${signed(c.delta, isModified)}`}</title>
              </rect>
            );
          })}
        </g>

        {/* x-axis labels (rotated) */}
        <g>
          {cols.map((c) => (
            <text
              key={`xl-${c.key}`}
              transform={`translate(${c.cx}, ${HEIGHT - MARGIN.bottom + 12}) rotate(25)`}
              className={
                c.kind === "instrument"
                  ? "beamm-waterfall__xlabel"
                  : "beamm-waterfall__xlabel beamm-waterfall__xlabel--emph"
              }
            >
              {xLabelLines(c).map((line, i) => (
                <tspan key={i} x={0} dy={i === 0 ? 0 : "1.1em"}>
                  {line}
                </tspan>
              ))}
            </text>
          ))}
        </g>
      </svg>

      {hover && (
        <div
          className="beamm-waterfall__tooltip"
          data-testid="budget-tooltip"
          style={{ left: hover.x, top: hover.y }}
        >
          <Tooltip col={hover.col} isModified={isModified} />
        </div>
      )}
    </div>
  );
}

function Tooltip({
  col,
  isModified,
}: {
  col: Column;
  isModified: boolean;
}): JSX.Element {
  if (col.kind === "instrument") {
    return (
      <>
        <strong>{col.label}</strong>
        <div className="beamm-waterfall__tooltip-tier">
          <span
            className="beamm-waterfall__tooltip-dot"
            style={{ background: col.level ? TIER_COLOURS[col.level] : "#000" }}
          />
          {col.level ? TIER_LABELS[col.level] : ""}
        </div>
        <div>baseline: {formatMillionsEur(col.baselineEur ?? 0)}</div>
        <div>reform: {formatMillionsEur(col.reformEur ?? 0)}</div>
        <div>change: {isModified ? signed(col.delta, true) : "baseline"}</div>
      </>
    );
  }
  const heading = col.kind === "net" ? "Net" : col.label;
  return (
    <>
      <strong>{heading}</strong>
      <div>{isModified ? signed(col.delta, true) : "baseline"}</div>
    </>
  );
}

function sortInstruments(instruments: InstrumentBudget[]): OrderedInstrument[] {
  const enriched: OrderedInstrument[] = instruments.map((i) => ({
    ...i,
    label: INSTRUMENT_LABELS[i.instrument] ?? i.instrument,
  }));
  enriched.sort((a, b) => {
    const ai = TIER_ORDER.indexOf(a.level);
    const bi = TIER_ORDER.indexOf(b.level);
    if (ai !== bi) return ai - bi;
    if (a.delta_eur !== b.delta_eur) return b.delta_eur - a.delta_eur;
    return Math.abs(b.baseline_eur) - Math.abs(a.baseline_eur);
  });
  return enriched;
}

function sum(values: number[]): number {
  return values.reduce((acc, v) => acc + v, 0);
}

// Label lines under each bar (multi-line via tspans). Instruments use their
// short display label; net uses "Net"; subtotals read "Total" — the tier is
// carried by the background band, so it isn't repeated here.
function xLabelLines(c: Column): string[] {
  if (c.kind === "net") return ["Net"];
  if (c.kind === "subtotal") return ["Total"];
  return [INSTRUMENT_LABELS_SHORT[c.key] ?? c.label];
}

// Signed compact euro for tooltips / titles. U+2212 minus to match the UI.
function signed(value: number, isModified: boolean): string {
  if (!isModified || Math.abs(value) < 1) return "baseline";
  const s = value > 0 ? "+" : "−";
  return `${s}${formatMillionsEur(Math.abs(value))}`;
}

// Compact euros: "€18m", "−€4m", "€5.3bn".
function formatMillionsEur(value: number): string {
  const millions = value / 1_000_000;
  const abs = Math.abs(millions);
  const sign = millions < 0 ? "−" : "";
  if (abs >= 1000) return `${sign}€${(abs / 1000).toFixed(2)}bn`;
  if (abs >= 1) return `${sign}€${abs.toFixed(0)}m`;
  if (abs > 0) return `${sign}€${(value / 1_000).toFixed(0)}k`;
  return "€0";
}

// Round a positive number up to the next 1/2/5 × 10^k so axis ticks land on
// clean values with a little headroom over the tallest bar.
function roundUpToNice(value: number): number {
  if (value <= 0) return 0;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalised = value / magnitude;
  let nice: number;
  if (normalised <= 1) nice = 1;
  else if (normalised <= 2) nice = 2;
  else if (normalised <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude;
}

// Y-axis tick formatter — sparser than the headline formatter; keeps signs.
function formatAxisMillions(value: number): string {
  if (Math.abs(value) < 1) return "0";
  const millions = value / 1_000_000;
  const abs = Math.abs(millions);
  const sign = millions < 0 ? "−" : "";
  if (abs >= 1000) return `${sign}€${(abs / 1000).toFixed(1)}bn`;
  if (abs >= 1) return `${sign}€${abs.toFixed(0)}m`;
  return `${sign}€${(value / 1_000).toFixed(0)}k`;
}
